import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import {
  getAndroidDeliveryStatus,
  getCreatedAtWithTime,
  getDeliveryStatus,
  getEmailDeliveryStatus,
  getIosDeliveryStatus,
  getNotificationPermissions,
  getSmsDeliveryStatus,
  getSchoolNameNotification
} from 'Src/helpers/accessor/accessor';
import { Notification } from 'Src/models/notification';
import { getSelectOptionsForGeneralMessagesDeliveryStatus } from 'Src/helpers/table/select';
import {
  getAllTournamentNotifications,
  getTournamentNotificationCampaignsPage,
  getTournamentNotificationCampaignNotificationsPage
} from 'Src/helpers/service/adminSU/tournamentNotificationsTable';
import { getTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { Tournament } from 'Src/models/tournament';
import { GENERAL_MESSAGE_DELIVERY_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/generalMessage';
import {
  getNotificationAudience,
  getNotificationAudienceLabel,
  getNotificationTypeLabel
} from 'Src/helpers/notification/notification';
import './Tournaments.css';
import './TournamentNotifications.scss';

const getNotificationTitle = (notification: Notification): string => {
  const content = notification.details && notification.details.content;
  return (
    notification.title ||
    (content && content.email && content.email.subject) ||
    (content && content.push && content.push.title) ||
    getNotificationTypeLabel(notification.type)
  );
};

const getNotificationBody = (notification: Notification): string => {
  const content = notification.details && notification.details.content;
  return (
    notification.body ||
    (content && content.email && content.email.body) ||
    (content && content.push && content.push.body) ||
    (content && content.sms && content.sms.body) ||
    ''
  );
};

const isLikelyHtml = (value: string): boolean => /<\/?(html|head|body)|<!doctype/i.test(value || '');

const getVisibleTextPreview = (value: string): string => {
  if (!value) return '';

  const parsedDocument = new DOMParser().parseFromString(value, 'text/html');
  Array.from(parsedDocument.querySelectorAll('style, script, head, title, template, noscript')).forEach(node => {
    if (node.parentNode) node.parentNode.removeChild(node);
  });

  return (parsedDocument.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const CHANNEL_LABELS = {
  DEFAULT_EMAIL: 'Email',
  MIS_EMAIL: 'Email',
  DEFAULT_SMS: 'SMS',
  MIS_SMS: 'SMS',
  IOS: 'iOS push',
  ANDROID: 'Android push',
  EMAIL: 'Email',
  SMS: 'SMS',
  PUSH: 'Push'
};

const getChannelGroup = (channel: string): string => {
  if (['DEFAULT_EMAIL', 'MIS_EMAIL'].indexOf(channel) !== -1) return 'EMAIL';
  if (['DEFAULT_SMS', 'MIS_SMS'].indexOf(channel) !== -1) return 'SMS';
  if (['IOS', 'ANDROID'].indexOf(channel) !== -1) return 'PUSH';
  return channel;
};

const getStatusClass = (value: string): string =>
  String(value || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');

const renderStatusCell = (value: string): JSX.Element => (
  <td className="tournamentNotifications__statusCell">
    {value ? <span className={`is-${getStatusClass(value)}`}>{value}</span> : <span className="is-empty">—</span>}
  </td>
);

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Notification',
    field: 'title',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    minWidth: '260px',
    cell: (notification: Notification) => (
      <td className="tournamentNotifications__notificationCell">
        <strong>{getNotificationTitle(notification)}</strong>
        <span>{getNotificationTypeLabel(notification.type)}</span>
      </td>
    )
  },
  {
    text: 'Recipient',
    field: 'recipient',
    isSort: false,
    type: FILTER_TYPE.NONE,
    minWidth: '170px',
    cell: (notification: Notification) => {
      const firstName = notification.user && notification.user.firstName;
      const lastName = notification.user && notification.user.lastName;
      const initials = `${firstName ? firstName.charAt(0) : ''}${lastName ? lastName.charAt(0) : ''}` || '?';
      return (
        <td className="tournamentNotifications__recipientCell">
          <span>{initials}</span>
          <strong>{`${firstName || ''} ${lastName || ''}`.trim() || 'Unknown recipient'}</strong>
        </td>
      );
    }
  },
  {
    text: 'Name',
    field: 'user.firstName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['user', 'firstName']
  },
  {
    text: 'Surname',
    field: 'user.lastName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['user', 'lastName']
  },
  {
    text: 'School',
    field: 'schoolName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getSchoolNameNotification
  },
  {
    text: 'Role',
    field: 'preset',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getNotificationPermissions
  },
  {
    text: 'Sms',
    field: 'sms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (notification: Notification) => renderStatusCell(getSmsDeliveryStatus(notification))
  },
  {
    text: 'Email',
    field: 'email',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (notification: Notification) => renderStatusCell(getEmailDeliveryStatus(notification))
  },
  {
    text: 'Ios',
    field: 'ios',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (notification: Notification) => renderStatusCell(getIosDeliveryStatus(notification))
  },
  {
    text: 'Android',
    field: 'android',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (notification: Notification) => renderStatusCell(getAndroidDeliveryStatus(notification))
  },
  {
    text: 'Delivery Status',
    field: 'deliveryStatus',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    cell: (notification: Notification) => renderStatusCell(getDeliveryStatus(notification))
  },
  {
    text: 'Sent',
    field: 'createdAt',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAtWithTime
  }
];

interface State {
  items: Notification[];
  currentPage: number;
  selectedItems: Notification[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  tournament: Tournament;
  activeNotification?: Notification;
  loadError: string;
  campaigns: any[];
  campaignTotal: number;
  activeCampaign?: any;
  isRecipientList: boolean;
  deliveryChartChannel: string;
  isCampaignRefreshing: boolean;
  campaignRefreshError: string;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  embedded?: boolean;
}

export class TournamentNotifications extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      tournament: undefined,
      activeNotification: undefined,
      loadError: '',
      campaigns: [],
      campaignTotal: 0,
      activeCampaign: undefined,
      isRecipientList: false
      ,deliveryChartChannel: 'overview',
      isCampaignRefreshing: false,
      campaignRefreshError: ''
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onDocumentKeyDown);
    this.setState({
      isLoading: true
    });

    // The tournament header and the paged notification data are independent.
    // Loading them in parallel avoids holding the table behind the tournament request.
    this.setAdditionalItems().catch(() => undefined);
    this.setCampaigns().catch(() =>
      this.setState({ isLoading: false, loadError: 'We could not load the notification history. Please try again.' })
    );
  }

  setCampaigns = (page: number = FIRST_PAGE) => {
    const filter = getServerQueryFilter2(page, {}, 'createdAt DESC');
    return getTournamentNotificationCampaignsPage(this.props.user, this.getTournamentId(), filter).then(result => {
      this.setState({
        campaigns: result.items || [],
        campaignTotal: result.count || 0,
        currentPage: page,
        isLoading: false,
        loadError: ''
      });
      return true;
    });
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onDocumentKeyDown);
    if (!this.props.embedded) window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state.activeNotification) {
      this.closeNotificationDetails();
    } else if (event.key === 'Escape' && this.state.activeCampaign) {
      this.closeCampaign();
    }
  };

  setAdditionalItems() {
    const { user } = this.props;
    const tournamentId = this.getTournamentId();
    const routeState = (this.props.location.state || {}) as any;
    const suppliedTournament = routeState.tournament as Tournament | undefined;

    const applyTournament = (tournament: Tournament) => {
      this.setState({ tournament });

      if (!this.props.embedded) window.dispatchEvent(
        new CustomEvent('sit:topbar-breadcrumb', {
          detail: `Tournaments / ${tournament.name} / Notification history`
        })
      );

      return true;
    };

    if (suppliedTournament && String(suppliedTournament.id) === String(tournamentId)) {
      return Promise.resolve(applyTournament(suppliedTournament));
    }

    return getTournament(user, tournamentId).then(tournament => {
      return applyTournament(tournament);
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      const request = this.state.isRecipientList ? this.setItems() : this.setCampaigns();
      request
        .then(() => this.setState({ isLoading: false }))
        .catch(() =>
          this.setState({ isLoading: false, loadError: 'We could not load the notification history. Please try again.' })
        );
    }
  }

  getTournamentId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    return String(search.tournamentId || search.tournament || '');
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const tournamentId = this.getTournamentId();

    const actionDescriptorId = this.state.activeCampaign && (this.state.activeCampaign.id || this.state.activeCampaign._id);
    if (!actionDescriptorId) return Promise.resolve(false);
    return getTournamentNotificationCampaignNotificationsPage(user, tournamentId, actionDescriptorId, serverQueryFilter).then(result => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: result.items,
        total: result.count,
        basePath: history.location.pathname,
        isLoading: false,
        loadError: ''
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection, basePath } = this.state;
    const { history } = this.props;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    history.push({
      pathname: basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  openCampaign = (campaign: any): void => {
    this.setState({
      activeCampaign: campaign,
      activeNotification: undefined,
      deliveryChartChannel: 'overview',
      campaignRefreshError: ''
    });
  };

  closeCampaign = (): void => {
    this.setState({ activeCampaign: undefined, activeNotification: undefined, campaignRefreshError: '' });
  };

  refreshCampaignDelivery = (): void => {
    const { activeCampaign, currentPage, isCampaignRefreshing } = this.state;
    const campaignId = activeCampaign && (activeCampaign.id || activeCampaign._id);
    if (!campaignId || isCampaignRefreshing) return;

    this.setState({ isCampaignRefreshing: true, campaignRefreshError: '' });
    const filter = getServerQueryFilter2(currentPage, {}, 'createdAt DESC');
    getTournamentNotificationCampaignsPage(this.props.user, this.getTournamentId(), filter)
      .then(result => {
        const campaigns = result.items || [];
        const refreshedCampaign = campaigns.find(campaign => String(campaign.id || campaign._id) === String(campaignId));
        if (!refreshedCampaign) throw new Error('Campaign not found');

        this.setState({
          campaigns,
          campaignTotal: result.count || 0,
          activeCampaign: refreshedCampaign,
          isCampaignRefreshing: false
        });
      })
      .catch(() => this.setState({
        isCampaignRefreshing: false,
        campaignRefreshError: 'Could not refresh delivery statistics. Please try again.'
      }));
  };

  showCampaignRecipients = (): void => {
    this.setState({ isLoading: true, isRecipientList: true, activeNotification: undefined }, () => {
      this.setItems().catch(() =>
        this.setState({ isLoading: false, loadError: 'We could not load the recipient details. Please try again.' })
      );
    });
  };

  backToCampaigns = (): void => {
    this.setState({ isRecipientList: false, activeCampaign: undefined, activeNotification: undefined, currentPage: FIRST_PAGE });
  };

  onItemClick = (index: number): void => {
    const { items } = this.state;
    const selectedItem = items[index];

    this.setState({
      selectedItems: [selectedItem],
      activeNotification: selectedItem
    });
  };

  closeNotificationDetails = (): void => {
    this.setState({ activeNotification: undefined, selectedItems: [] });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const tournamentId = this.getTournamentId();

    getAllTournamentNotifications(user, tournamentId, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      activeNotification: undefined
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters, basePath } = this.state;
    const { history } = this.props;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    history.push({
      pathname: basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { history } = this.props;

    const { filters, sortDirection, sortColumnsName, basePath } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      activeNotification: undefined
    });

    const tournamentId = this.getTournamentId();

    history.push({
      pathname: basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onClearFilterClick = (): void => {
    const { history } = this.props;
    const { basePath } = this.state;

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      activeNotification: undefined
    });

    const tournamentId = this.getTournamentId();

    history.push({
      pathname: basePath,
      search: `tournament=${tournamentId}`,
      state: history.location.state
    });
  };

  onCancelFilterClick = (): void => {
    this.setState({ isShowFilter: false, filters: getFilters2(COLUMNS, parse(this.props.history.location.search)) });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  goBack = () => {
    const { history, location } = this.props;
    const state = (location.state || {}) as any;
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: state.prevPath || '/tournaments/manage/overview',
      search: state.search || `tournament=${tournamentId}`,
      state: { tournamentId: tournamentId }
    });
  };

  renderNotificationDetails(): React.ReactNode {
    const notification = this.state.activeNotification;
    if (!notification) return null;

    const recipient = notification.user
      ? `${notification.user.firstName || ''} ${notification.user.lastName || ''}`.trim()
      : 'Unknown recipient';
    const title = getNotificationTitle(notification);
    const body = getNotificationBody(notification);
    const type = getNotificationTypeLabel(notification.type);
    const audience = getNotificationAudience(notification.type);
    const deliveryStatus =
      GENERAL_MESSAGE_DELIVERY_STATUS_SERVER_TO_CLIENT_MAPPING[notification.deliveryStatus] ||
      notification.deliveryStatus ||
      'Not available';
    const channels = Array.isArray(notification.channelStatus)
      ? notification.channelStatus.filter(channel => channel && channel.channelType)
      : [];

    return (
      <div className="tournamentNotifications__backdrop" onMouseDown={this.closeNotificationDetails}>
        <section
          className="tournamentNotifications__drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tournament-notification-title"
          onMouseDown={event => event.stopPropagation()}
        >
          <header className="tournamentNotifications__drawerHeader">
            <div>
              <span>Tournament notification</span>
              <h2 id="tournament-notification-title">{title}</h2>
            </div>
            <button type="button" aria-label="Close notification details" onClick={this.closeNotificationDetails}>
              ×
            </button>
          </header>

          <div className="tournamentNotifications__drawerBody">
            <div className="tournamentNotifications__badges">
              <span>{type}</span>
              <span className={audience === 'ORGANISER' ? 'is-organiser' : ''}>
                {getNotificationAudienceLabel(audience)}
              </span>
              <span className="is-status">{deliveryStatus}</span>
            </div>

            <dl className="tournamentNotifications__summary">
              <div>
                <dt>Recipient</dt>
                <dd>{recipient}</dd>
              </div>
              <div>
                <dt>School</dt>
                <dd>{getSchoolNameNotification(notification) || '—'}</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>{getNotificationPermissions(notification) || '—'}</dd>
              </div>
              <div>
                <dt>Sent</dt>
                <dd>{getCreatedAtWithTime(notification) || '—'}</dd>
              </div>
            </dl>

            <section className="tournamentNotifications__section">
              <h3>Delivery channels</h3>
              {channels.length > 0 ? (
                <div className="tournamentNotifications__channels">
                  {channels.map((channel, index) => {
                    const channelStatus =
                      GENERAL_MESSAGE_DELIVERY_STATUS_SERVER_TO_CLIENT_MAPPING[channel.deliveryStatus] ||
                      channel.deliveryStatus ||
                      'Not available';
                    const destination =
                      channel.channelType === 'DEFAULT_EMAIL'
                        ? notification.user && notification.user.email
                        : channel.channelType === 'DEFAULT_SMS'
                        ? notification.user && notification.user.phone
                        : channel.channelId;
                    return (
                      <div key={`${channel.channelId || channel.channelType}-${index}`}>
                        <strong>{CHANNEL_LABELS[channel.channelType] || channel.channelType}</strong>
                        <span>{destination || '—'}</span>
                        <b>{channelStatus}</b>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="tournamentNotifications__muted">No delivery channel information is available.</p>
              )}
            </section>

            <section className="tournamentNotifications__section">
              <h3>Message</h3>
              {!body ? (
                <p className="tournamentNotifications__muted">No message content is available.</p>
              ) : isLikelyHtml(body) ? (
                <iframe title="Notification content" sandbox="" srcDoc={body} />
              ) : (
                <div className="tournamentNotifications__message">{body}</div>
              )}
            </section>
          </div>
        </section>
      </div>
    );
  }

  getCampaignDelivery(campaign: any) {
    const overall = (((campaign || {}).delivery || {}).overall || []) as any[];
    const channels = (((campaign || {}).delivery || {}).channels || []) as any[];
    const statusCount = (rows: any[], statuses: string[]) =>
      rows.reduce((total, row) => (statuses.indexOf(String(row._id && (row._id.status || row._id))) !== -1 ? total + row.count : total), 0);
    const total = Number((((campaign || {}).delivery || {}).total || [])[0]?.count || 0);
    return {
      total,
      delivered: statusCount(overall, ['DELIVERED', 'READ']),
      sent: statusCount(overall, ['SENT']),
      failed: statusCount(overall, ['ERROR', 'NOT_SENT']),
      pending: statusCount(overall, ['NONE', 'SCHEDULED', 'SENDING']),
      unavailable: statusCount(overall, ['NO_CHANNELS', 'UNSUPPORTED_CHANNEL_TYPE']),
      channels
    };
  }

  getCampaignPresentation(campaign: any) {
    const sample = campaign && campaign.delivery && campaign.delivery.sample && campaign.delivery.sample[0];
    const isCustom = sample && sample.type === 'TOURNAMENT_CUSTOM';
    const content = (campaign && campaign.content) || (sample && sample.details && sample.details.content) || {};
    return {
      sample,
      type: sample ? getNotificationTypeLabel(sample.type) : 'Tournament notification',
      audience: campaign.recipientAudience === 'ATHLETE_SCHOOLS' ? 'Athletes’ schools' : sample ? getNotificationAudienceLabel(getNotificationAudience(sample.type)) : '',
      title: isCustom ? campaign.title || (sample && sample.title) || (content.push && content.push.title) : sample ? getNotificationTypeLabel(sample.type) : campaign.title,
      body:
        (content.email && content.email.body) ||
        campaign.text ||
        (sample && sample.body) ||
        (content.push && content.push.body) ||
        (content.sms && content.sms.body) ||
        '',
      content
    };
  }

  renderCampaignDetails(): React.ReactNode {
    const campaign = this.state.activeCampaign;
    if (!campaign || this.state.isRecipientList) return null;
    const stats = this.getCampaignDelivery(campaign);
    const presentation = this.getCampaignPresentation(campaign);
    const channelNames = Array.from(
      new Set(stats.channels.map(channel => getChannelGroup(String(channel._id && channel._id.type))).filter(Boolean))
    ) as string[];
    const content = presentation.content;
    const body = presentation.body;
    const attachments = Array.isArray(campaign.attachments) ? campaign.attachments : [];
    const selectedChannel = this.state.deliveryChartChannel;
    const selectedRows = selectedChannel === 'overview' ? [] : stats.channels.filter(row => row._id && getChannelGroup(row._id.type) === selectedChannel);
    const countStatuses = (statuses: string[]) => selectedRows.reduce((sum, row) => statuses.indexOf(row._id.status) !== -1 ? sum + row.count : sum, 0);
    const chartStats = selectedChannel === 'overview' ? stats : {
      total: selectedRows.reduce((sum, row) => sum + row.count, 0),
      delivered: countStatuses(['DELIVERED', 'READ']), sent: countStatuses(['SENT']),
      failed: countStatuses(['ERROR', 'NOT_SENT']), pending: countStatuses(['NONE', 'SCHEDULED', 'SENDING']),
      unavailable: countStatuses(['NO_CHANNELS', 'UNSUPPORTED_CHANNEL_TYPE'])
    };
    const total = Math.max(chartStats.total, 1);
    const deliveredDeg = Math.round((chartStats.delivered / total) * 360);
    const sentDeg = Math.round((chartStats.sent / total) * 360);
    const failedDeg = Math.round((chartStats.failed / total) * 360);
    const unavailableDeg = Math.round((chartStats.unavailable / total) * 360);
    const { isCampaignRefreshing, campaignRefreshError } = this.state;

    return (
      <div className="tournamentNotifications__backdrop" onMouseDown={this.closeCampaign}>
        <section className="tournamentNotifications__drawer tournamentNotifications__drawer--campaign" onMouseDown={event => event.stopPropagation()}>
          <header className="tournamentNotifications__drawerHeader">
            <div><span>{presentation.type}</span><h2>{presentation.title || 'Tournament notification'}</h2></div>
            <button type="button" aria-label="Close message summary" onClick={this.closeCampaign}>×</button>
          </header>
          <div className="tournamentNotifications__drawerBody">
            <div className="tournamentNotifications__badges">
              {channelNames.map(channel => <span key={channel}>{CHANNEL_LABELS[channel] || channel}</span>)}
              {presentation.audience && <span className="is-organiser">{presentation.audience}</span>}
              <span className="is-status">{stats.total} {stats.total === 1 ? 'recipient' : 'recipients'}</span>
            </div>
            <section className="tournamentNotifications__section">
              <div className="tournamentNotifications__deliveryToolbar">
                <h3>Delivery overview</h3>
                <button
                  type="button"
                  className="tournamentNotifications__refreshDelivery"
                  onClick={this.refreshCampaignDelivery}
                  disabled={isCampaignRefreshing}
                  title="Refresh delivery statistics"
                >
                  <span aria-hidden="true">↻</span> {isCampaignRefreshing ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              {campaignRefreshError && <p className="tournamentNotifications__refreshError" role="alert">{campaignRefreshError}</p>}
              <div className="tournamentNotifications__deliveryTabs">
                {['overview', ...channelNames].map(channel => <button type="button" key={channel} className={selectedChannel === channel ? 'is-active' : ''} onClick={() => this.setState({ deliveryChartChannel: channel })}>{channel === 'overview' ? 'Overview' : CHANNEL_LABELS[channel] || channel}</button>)}
              </div>
              <div className="tournamentNotifications__deliveryChart">
                <div className="tournamentNotifications__donut" style={{ background: `conic-gradient(#35a66f 0 ${deliveredDeg}deg, #3478c5 ${deliveredDeg}deg ${deliveredDeg + sentDeg}deg, #d6455d ${deliveredDeg + sentDeg}deg ${deliveredDeg + sentDeg + failedDeg}deg, #9aa8b5 ${deliveredDeg + sentDeg + failedDeg}deg ${deliveredDeg + sentDeg + failedDeg + unavailableDeg}deg, #dce5ec ${deliveredDeg + sentDeg + failedDeg + unavailableDeg}deg 360deg)` }}>
                  <div><strong>{chartStats.total}</strong><span>Total</span></div>
                </div>
                <div className="tournamentNotifications__legend">
                  <span className="is-delivered">Delivered <b>{chartStats.delivered}</b></span>
                  <span className="is-sent">Sent <b>{chartStats.sent}</b></span>
                  <span className="is-failed">Failed <b>{chartStats.failed}</b></span>
                  <span className="is-pending">Pending <b>{chartStats.pending}</b></span>
                  {chartStats.unavailable > 0 && <span className="is-unavailable">Unavailable <b>{chartStats.unavailable}</b></span>}
                </div>
              </div>
              <div className="tournamentNotifications__channelBreakdown">
                {channelNames.map(channel => {
                  const rows = stats.channels.filter(row => row._id && getChannelGroup(row._id.type) === channel);
                  const count = (statuses: string[]) => rows.reduce((sum, row) => statuses.indexOf(row._id.status) !== -1 ? sum + row.count : sum, 0);
                  return <div key={channel}><strong>{CHANNEL_LABELS[channel] || channel}</strong><span>{count(['DELIVERED', 'READ'])} delivered</span><span>{count(['ERROR', 'NOT_SENT'])} failed</span></div>;
                })}
              </div>
            </section>
            <section className="tournamentNotifications__section">
              <h3>Message content</h3>
              {content.email && content.email.subject ? <p className="tournamentNotifications__subject">{content.email.subject}</p> : null}
              {isLikelyHtml(body) ? <iframe title="Message content" sandbox="" srcDoc={body} /> : <div className="tournamentNotifications__message">{body || 'No message content is available.'}</div>}
              {attachments.length > 0 && <div className="tournamentNotifications__attachments">{attachments.map((file, index) => <span key={`${file.externalFileId || file.fileName}-${index}`}>📎 {file.fileName || 'Attachment'}</span>)}</div>}
            </section>
            <button type="button" className="tournamentNotifications__viewRecipients" onClick={this.showCampaignRecipients}>View recipient details</button>
          </div>
        </section>
      </div>
    );
  }

  renderCampaignList(): React.ReactNode {
    const { campaigns, campaignTotal, currentPage } = this.state;
    const pageCount = Math.max(1, Math.ceil(campaignTotal / LIMIT));
    return (
      <div className="tournamentNotifications__campaigns">
        <div className="tournamentNotifications__campaignCount">{campaignTotal} {campaignTotal === 1 ? 'message' : 'messages'}</div>
        {campaigns.length === 0 ? <div className="tournamentNotifications__empty"><strong>No messages found</strong><span>Messages sent for this tournament will appear here.</span></div> : campaigns.map(campaign => {
          const stats = this.getCampaignDelivery(campaign);
          const presentation = this.getCampaignPresentation(campaign);
          const body = presentation.body;
          const bodyPreview = getVisibleTextPreview(String(body || ''));
          return <button type="button" className="tournamentNotifications__campaign" key={campaign.id || campaign._id} onClick={() => this.openCampaign(campaign)}>
            <div><strong>{presentation.title || 'Tournament notification'}</strong><span>{presentation.type}{presentation.audience ? ` · ${presentation.audience}` : ''}{bodyPreview ? ` · ${bodyPreview}` : ''}</span></div>
            <div className="tournamentNotifications__campaignMetrics"><span>{stats.total} recipients</span><span className="is-success">{stats.delivered} delivered</span>{stats.failed > 0 && <span className="is-danger">{stats.failed} failed</span>}<time>{getCreatedAtWithTime(campaign)}</time></div>
          </button>;
        })}
        {pageCount > 1 && <div className="tournamentNotifications__pagination"><Pagination2 currentPage={currentPage} total={campaignTotal} setCurrentPageParams={this.setCampaigns} modern /></div>}
      </div>
    );
  }

  renderNotificationTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total
    } = this.state;

    const actionItems = [];

    const filterOptions = {
      deliveryStatus: getSelectOptionsForGeneralMessagesDeliveryStatus()
    };

    return (
      <Grid2
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={`${total} ${total === 1 ? 'notification' : 'notifications'}`}
        hiddenFilterFields={['title']}
        columnVisibilityKey="tournament-notification-history"
        fixedColumnFields={['title', 'recipient', 'deliveryStatus', 'createdAt']}
        defaultHiddenColumns={['ios', 'android']}
        filterOnlyFields={['user.firstName', 'user.lastName']}
        emptyText="No notifications found"
        emptyHint="Notifications sent for this tournament will appear here."
        isCSV={false}
        isActionsInline
        isSearchHidden
        isSelectionHidden
        user={this.props.user}
      />
    );
  }

  render() {
    const { isLoading, tournament, loadError, isRecipientList, activeCampaign } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="tournamentNotifications unionTournamentsModern unionTournamentsListShell">
        <header className="tournamentNotifications__pageHeader">
          {(!this.props.embedded || isRecipientList) && <button type="button" className="tournamentNotifications__back" onClick={isRecipientList ? this.backToCampaigns : this.goBack}>
            <span aria-hidden="true">←</span> {isRecipientList ? 'Back to messages' : 'Back to tournament'}
          </button>}
          <div>
            <h1>Notification history</h1>
            <p>
              {tournament ? tournament.name : 'Tournament'} · {isRecipientList && activeCampaign ? `Recipients for ${activeCampaign.title || 'message'}` : 'Review sent messages and delivery performance.'}
            </p>
          </div>
        </header>
        {loadError ? (
          <div className="tournamentNotifications__loadError" role="alert">
            {loadError}
            <button
              type="button"
              onClick={() =>
                this.setState({ isLoading: true, loadError: '' }, () =>
                  (isRecipientList ? this.setItems() : this.setCampaigns()).catch(() =>
                    this.setState({
                      isLoading: false,
                      loadError: 'We could not load the notification history. Please try again.'
                    })
                  )
                )
              }
            >
              Try again
            </button>
          </div>
        ) : null}
        {isRecipientList ? <div className="tournamentNotifications__card unionTournamentsListCard">{this.renderNotificationTable()}</div> : this.renderCampaignList()}
        {this.renderCampaignDetails()}
        {this.renderNotificationDetails()}
      </div>
    );
  }
}

